import { authService } from './authService';
import { API_BASE_URL } from '../config/api';

export interface Refund {
  id: string;
  orderId: string;
  reason: string;
  amount: number;
  acceptedTerms?: boolean;
  acceptedTermsAt?: string;
  status: 'pending' | 'approved' | 'rejected' | 'processed';
  adminResponse?: string;
  createdAt: string;
  updatedAt: string;
  buyerId?: string;
  buyerEmail?: string;
  buyerName?: string;
}

const baseUrl = API_BASE_URL;
const REFUND_STORAGE_KEY = 'haapa.refunds';

interface StoredRefund extends Refund {}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim()
    ? value.trim()
    : typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : undefined;

const getFirstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    const resolved = asString(value);
    if (resolved) {
      return resolved;
    }
  }
  return undefined;
};

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const toAbsoluteUrl = (base: string, path: string): string => {
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${normalizedBase}${path.startsWith('/') ? path : `/${path}`}`;
};

const getBaseCandidates = (): string[] => {
  const trimmedBase = baseUrl.replace(/\/+$/, '');
  const candidates: string[] = [];

  const add = (value: string) => {
    const normalized = value.replace(/\/+$/, '');
    if (normalized && !candidates.includes(normalized)) {
      candidates.push(normalized);
    }
  };

  add(trimmedBase);

  if (!/\/api(?:\/v1)?$/i.test(trimmedBase)) {
    add(`${trimmedBase}/api`);
    add(`${trimmedBase}/api/v1`);
  } else if (/\/api$/i.test(trimmedBase)) {
    add(`${trimmedBase}/v1`);
  } else if (/\/api\/v1$/i.test(trimmedBase)) {
    add(trimmedBase.replace(/\/api\/v1$/i, '/api'));
  }

  return candidates;
};

const buildHeaders = (withJson = false): HeadersInit => {
  const headers: Record<string, string> = {
    Accept: '*/*',
  };

  if (withJson) {
    headers['Content-Type'] = 'application/json';
  }

  const token = authService.getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const storedUser = authService.getStoredUser();
  if (storedUser?.id) {
    headers['x-user-id'] = storedUser.id;
  }
  if (storedUser?.role) {
    headers['x-user-role'] = storedUser.role === 'customer' ? 'buyer' : storedUser.role;
  }

  return headers;
};

const toErrorMessage = (payload: unknown, fallback: string): string => {
  const record = asRecord(payload);
  const dataRecord = asRecord(record.data);
  const recordError = record.error;
  const dataError = dataRecord.error;
  const errorFromArray =
    (Array.isArray(recordError) ? asString(recordError[0]) : undefined) ||
    (Array.isArray(dataError) ? asString(dataError[0]) : undefined);
  return (
    asString(record.message) ||
    errorFromArray ||
    asString(record.error) ||
    asString(dataRecord.message) ||
    asString(dataRecord.error) ||
    fallback
  );
};

const normalizeRefund = (value: unknown): Refund | null => {
  const record = asRecord(value);
  const buyerRecord = asRecord(record.buyer);
  const customerRecord = asRecord(record.customer);
  const userRecord = asRecord(record.user);
  const id = asString(record.id) || asString(record._id);
  const orderId = asString(record.orderId) || asString(record.order_id);
  const reason =
    asString(record.reason) ||
    asString(record.refundReason) ||
    asString(record.complaint) ||
    asString(record.message);
  const amount = asNumber(record.amount);
  const statusRaw = asString(record.status)?.toLowerCase();
  const acceptedTerms =
    typeof record.acceptedTerms === 'boolean'
      ? record.acceptedTerms
      : typeof record.conditionsAccepted === 'boolean'
      ? record.conditionsAccepted
      : undefined;
  const buyerId =
    getFirstString(
      record.buyerId,
      record.userId,
      record.customerId,
      buyerRecord.id,
      buyerRecord._id,
      buyerRecord.userId,
      customerRecord.id,
      customerRecord._id,
      customerRecord.userId,
      userRecord.id,
      userRecord._id,
      userRecord.userId,
    );
  const buyerEmail =
    getFirstString(
      record.buyerEmail,
      record.email,
      buyerRecord.email,
      customerRecord.email,
      userRecord.email,
    );
  const buyerName =
    getFirstString(
      record.buyerName,
      record.customerName,
      buyerRecord.name,
      buyerRecord.fullname,
      buyerRecord.fullName,
      buyerRecord.username,
      customerRecord.name,
      customerRecord.fullname,
      customerRecord.fullName,
      customerRecord.username,
      userRecord.name,
      userRecord.fullname,
      userRecord.fullName,
      userRecord.username,
    );

  if (!id || !orderId || !reason || amount === undefined || !statusRaw) {
    return null;
  }

  const status: Refund['status'] =
    statusRaw === 'approved' || statusRaw === 'rejected' || statusRaw === 'processed'
      ? statusRaw
      : 'pending';

  return {
    id,
    orderId,
    reason,
    amount,
    ...(acceptedTerms !== undefined ? { acceptedTerms } : {}),
    acceptedTermsAt:
      asString(record.acceptedTermsAt) ||
      asString(record.conditionsAcceptedAt),
    status,
    adminResponse: asString(record.adminResponse) || asString(record.admin_response),
    createdAt: asString(record.createdAt) || new Date().toISOString(),
    updatedAt: asString(record.updatedAt) || asString(record.createdAt) || new Date().toISOString(),
    ...(buyerId ? { buyerId } : {}),
    ...(buyerEmail ? { buyerEmail } : {}),
    ...(buyerName ? { buyerName } : {}),
  };
};

const normalizeStoredRefund = (value: unknown): StoredRefund | null => {
  const normalized = normalizeRefund(value);
  if (!normalized) {
    return null;
  }

  const record = asRecord(value);
  const buyerId =
    asString(record.buyerId) ||
    asString(record.userId) ||
    asString(record.customerId);
  const buyerEmail = asString(record.buyerEmail) || asString(record.email);
  const buyerName =
    getFirstString(
      record.buyerName,
      record.customerName,
      record.fullname,
      record.fullName,
      record.name,
      record.username,
    );

  return {
    ...normalized,
    ...(buyerId ? { buyerId } : {}),
    ...(buyerEmail ? { buyerEmail } : {}),
    ...(buyerName ? { buyerName } : {}),
  };
};

const readStoredRefunds = (): StoredRefund[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(REFUND_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((entry) => normalizeStoredRefund(entry))
      .filter((entry): entry is StoredRefund => Boolean(entry));
  } catch {
    return [];
  }
};

const writeStoredRefunds = (refunds: StoredRefund[]) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(REFUND_STORAGE_KEY, JSON.stringify(refunds));
};

const upsertStoredRefund = (refund: StoredRefund) => {
  const existing = readStoredRefunds();
  const index = existing.findIndex((entry) => entry.id === refund.id);
  const mergedRefund =
    index >= 0 ? { ...existing[index], ...refund } : refund;
  const next = index >= 0
    ? [...existing.slice(0, index), mergedRefund, ...existing.slice(index + 1)]
    : [mergedRefund, ...existing];
  writeStoredRefunds(next);
};

const mergeRefundLists = (primary: Refund[], secondary: StoredRefund[]): Refund[] => {
  const merged = new Map<string, Refund>();

  secondary.forEach((refund) => {
    merged.set(refund.id, refund);
  });

  primary.forEach((refund) => {
    merged.set(refund.id, refund);
  });

  return Array.from(merged.values());
};

const filterRefundsForUser = (refunds: StoredRefund[], userId?: string, email?: string): StoredRefund[] => {
  if (!userId && !email) {
    return [];
  }

  const normalizedEmail = email?.trim().toLowerCase();
  return refunds.filter((refund) => {
    if (userId && refund.buyerId === userId) {
      return true;
    }

    if (normalizedEmail && refund.buyerEmail?.toLowerCase() === normalizedEmail) {
      return true;
    }

    return false;
  });
};

const normalizeRefundList = (payload: unknown): Refund[] => {
  const payloadRecord = asRecord(payload);
  const dataRecord = asRecord(payloadRecord.data);

  const listCandidates = [
    asArray(payload),
    asArray(payloadRecord.data),
    asArray(payloadRecord.items),
    asArray(payloadRecord.refunds),
    asArray(payloadRecord.results),
    asArray(dataRecord.data),
    asArray(dataRecord.items),
    asArray(dataRecord.refunds),
    asArray(dataRecord.results),
  ];

  const rawList = listCandidates.find((candidate) => candidate.length > 0) ?? [];
  return rawList
    .map((entry) => normalizeRefund(entry))
    .filter((entry): entry is Refund => Boolean(entry));
};

const normalizeSingleRefund = (payload: unknown): Refund => {
  const payloadRecord = asRecord(payload);
  const dataRecord = asRecord(payloadRecord.data);

  const candidates = [payload, payloadRecord.refund, dataRecord.refund, dataRecord.item, payloadRecord.item];
  for (const candidate of candidates) {
    const normalized = normalizeRefund(candidate);
    if (normalized) {
      return normalized;
    }
  }

  throw new Error('Refund payload is invalid.');
};

const parseResponse = async (response: Response, fallback: string): Promise<unknown> => {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(toErrorMessage(payload, fallback));
  }

  return payload;
};

const requestWithFallback = async (path: string, init: RequestInit, fallback: string): Promise<unknown> => {
  const baseCandidates = getBaseCandidates();
  let lastError: Error | null = null;

  for (let index = 0; index < baseCandidates.length; index += 1) {
    const baseCandidate = baseCandidates[index];
    const hasNextCandidate = index < baseCandidates.length - 1;
    try {
      const response = await fetch(toAbsoluteUrl(baseCandidate, path), init);

      if ((response.status === 404 || response.status === 405) && hasNextCandidate) {
        continue;
      }

      return parseResponse(response, fallback);
    } catch (error: unknown) {
      lastError = error instanceof Error ? error : new Error(fallback);
    }
  }

  throw lastError ?? new Error(fallback);
};

const requestWithPathFallback = async (paths: string[], init: RequestInit, fallback: string): Promise<unknown> => {
  let lastError: Error | null = null;

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index];

    try {
      return await requestWithFallback(path, init, fallback);
    } catch (error: unknown) {
      lastError = error instanceof Error ? error : new Error(fallback);
    }
  }

  throw lastError ?? new Error(fallback);
};

export const refundService = {
  async requestRefund(
    orderId: string,
    reason: string,
    amount: number,
    acceptedTerms = false,
  ): Promise<Refund> {
    const payload = await requestWithFallback(
      '/refunds',
      {
        method: 'POST',
        headers: buildHeaders(true),
        body: JSON.stringify({
          orderId,
          order_id: orderId,
          reason,
          refundReason: reason,
          amount,
          refundAmount: amount,
          acceptedTerms,
          conditionsAccepted: acceptedTerms,
          ...(acceptedTerms ? { acceptedTermsAt: new Date().toISOString() } : {}),
        }),
      },
      'Failed to request refund',
    );

    const normalized = normalizeSingleRefund(payload);
    const storedUser = authService.getStoredUser();
    const buyerName =
      storedUser?.fullname ||
      storedUser?.name ||
      storedUser?.username;
    upsertStoredRefund({
      ...normalized,
      buyerId: storedUser?.id,
      buyerEmail: storedUser?.email,
      ...(buyerName ? { buyerName } : {}),
    });
    return normalized;
  },

  async getMyRefunds(): Promise<Refund[]> {
    const storedUser = authService.getStoredUser();
    const buyerName =
      storedUser?.fullname ||
      storedUser?.name ||
      storedUser?.username;
    const localRefunds = filterRefundsForUser(readStoredRefunds(), storedUser?.id, storedUser?.email);

    try {
      const payload = await requestWithPathFallback(
        ['/refunds/my', '/refunds/me'],
        {
          headers: buildHeaders(),
        },
        'Failed to fetch refunds',
      );

      const apiRefunds = normalizeRefundList(payload);
      apiRefunds.forEach((refund) =>
        upsertStoredRefund({
          ...refund,
          buyerId: storedUser?.id,
          buyerEmail: storedUser?.email,
          ...(buyerName ? { buyerName } : {}),
        }),
      );
      return mergeRefundLists(apiRefunds, localRefunds);
    } catch {
      return localRefunds;
    }
  },

  async getAllRefunds(): Promise<Refund[]> {
    const localRefunds = readStoredRefunds();

    try {
      const payload = await requestWithPathFallback(
        ['/admin/refunds', '/refunds', '/refunds/all', '/manager/refunds'],
        {
          headers: buildHeaders(),
        },
        'Failed to fetch refunds',
      );

      const apiRefunds = normalizeRefundList(payload);
      apiRefunds.forEach((refund) => {
        const existing = localRefunds.find((entry) => entry.id === refund.id);
        const buyerId = existing?.buyerId ?? refund.buyerId;
        const buyerEmail = existing?.buyerEmail ?? refund.buyerEmail;
        const buyerName = existing?.buyerName ?? refund.buyerName;
        upsertStoredRefund({
          ...refund,
          ...(buyerId ? { buyerId } : {}),
          ...(buyerEmail ? { buyerEmail } : {}),
          ...(buyerName ? { buyerName } : {}),
        });
      });
      return mergeRefundLists(apiRefunds, localRefunds);
    } catch {
      return localRefunds;
    }
  },

  async updateRefundStatus(refundId: string, status: string, adminResponse?: string): Promise<Refund> {
    const encodedRefundId = encodeURIComponent(refundId);
    const payload = await requestWithPathFallback(
      [`/admin/refunds/${encodedRefundId}`, `/refunds/${encodedRefundId}`],
      {
        method: 'PUT',
        headers: buildHeaders(true),
        body: JSON.stringify({ status, adminResponse }),
      },
      'Failed to update refund',
    );

    const normalized = normalizeSingleRefund(payload);
    const localRefunds = readStoredRefunds();
    const existing = localRefunds.find((entry) => entry.id === normalized.id);
    const buyerId = existing?.buyerId ?? normalized.buyerId;
    const buyerEmail = existing?.buyerEmail ?? normalized.buyerEmail;
    const buyerName = existing?.buyerName ?? normalized.buyerName;
    upsertStoredRefund({
      ...normalized,
      ...(buyerId ? { buyerId } : {}),
      ...(buyerEmail ? { buyerEmail } : {}),
      ...(buyerName ? { buyerName } : {}),
    });
    return normalized;
  },
};
